import { getUrl } from 'aws-amplify/storage';
import { runtimeConfig } from '../config/runtime';
import type { LoadedBscData } from '../types/bsc';
import { buildLatestSnapshot } from './model';
import { buildBundleFromWorkbooks } from './workbook';

interface ExcelObject {
  key: string;
  localFileName: 'kpi_registry.xlsx' | 'kpi_values.xlsx';
}

const REGISTRY_OBJECT: ExcelObject = {
  key: runtimeConfig.registryKey,
  localFileName: 'kpi_registry.xlsx',
};

const VALUES_OBJECT: ExcelObject = {
  key: runtimeConfig.valuesKey,
  localFileName: 'kpi_values.xlsx',
};

async function downloadLocalExcel(
  object: ExcelObject,
  signal?: AbortSignal,
): Promise<ArrayBuffer> {
  const response = await fetch(`/__dev-data/${object.localFileName}`, {
    cache: 'no-store',
    signal,
  });
  if (!response.ok) {
    throw new Error(
      `Impossibile leggere ${object.localFileName} in locale (${response.status}).`,
    );
  }
  return response.arrayBuffer();
}

async function downloadS3Excel(
  object: ExcelObject,
  signal?: AbortSignal,
): Promise<ArrayBuffer> {
  const signed = await getUrl({
    path: object.key,
    options: {
      bucket: {
        bucketName: runtimeConfig.s3Bucket,
        region: runtimeConfig.s3Region,
      },
      validateObjectExistence: true,
      expiresIn: 60,
    },
  });

  const response = await fetch(signed.url, {
    cache: 'no-store',
    signal,
  });
  if (!response.ok) {
    throw new Error(
      `S3 ha restituito ${response.status} per l'oggetto ${object.key}.`,
    );
  }
  return response.arrayBuffer();
}

async function downloadExcel(
  object: ExcelObject,
  signal?: AbortSignal,
): Promise<ArrayBuffer> {
  return runtimeConfig.dataMode === 'local'
    ? downloadLocalExcel(object, signal)
    : downloadS3Excel(object, signal);
}

export async function loadBscData(signal?: AbortSignal): Promise<LoadedBscData> {
  const [registryBuffer, valuesBuffer] = await Promise.all([
    downloadExcel(REGISTRY_OBJECT, signal),
    downloadExcel(VALUES_OBJECT, signal),
  ]);

  const bundle = buildBundleFromWorkbooks(registryBuffer, valuesBuffer);
  if (bundle.registry.length === 0) {
    throw new Error('Il registry KPI è vuoto.');
  }

  return {
    bundle,
    snapshot: buildLatestSnapshot(bundle),
    loadedAt: new Date(),
  };
}
